import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Upload, CheckCircle2, Clock, XCircle, ArrowLeft, Camera, Eye, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { validateDocument } from '../lib/documents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientKYC() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [docType, setDocType] = useState('cedula');
  const [docNumber, setDocNumber] = useState('');
  const [imageFront, setImageFront] = useState('');
  const [imageBack, setImageBack] = useState('');
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone_whatsapp || '');
      setDocNumber(user.document_id || '');
      setDocType(user.document_type || 'cedula');
      setImageFront(user.document_image_front || '');
      setImageBack(user.document_image_back || '');
    }
  }, [user]);

  const handleUpload = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const isFront = side === 'front';
    if (isFront) setUploadingFront(true);
    else setUploadingBack(true);

    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: data,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error al subir imagen');
      if (isFront) setImageFront(json.url);
      else setImageBack(json.url);
    } catch (err) {
      setError(err.message || 'Error al cargar el archivo');
    } finally {
      if (isFront) setUploadingFront(false);
      else setUploadingBack(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!docNumber.trim()) {
      setError(t('clientKyc.docRequired', 'Por favor ingresa tu número de documento.'));
      return;
    }

    const docCheck = validateDocument(docNumber);
    if (!docCheck.valid && docType === 'cedula') {
      setError(t('checkout.invalidDocument', 'Documento inválido: usa una cédula dominicana o pasaporte válido.'));
      return;
    }

    if (!imageFront) {
      setError(t('clientKyc.frontImageRequired', 'Debes subir al menos la foto frontal de tu documento o licencia.'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          phone_whatsapp: phone.trim(),
          document_id: docNumber.trim(),
          document_image_front: imageFront,
          document_image_back: imageBack,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al guardar');
      
      await refreshUser();
      setSuccessMsg(t('clientKyc.submittedSuccess', 'Documentos enviados correctamente. Nuestro equipo revisará tu verificación en 24-48 horas.'));
    } catch (err) {
      setError(err.message || 'Error al enviar la verificación');
    } finally {
      setSubmitting(false);
    }
  };

  const isVerified = user?.verified;
  const isPending = !isVerified && (user?.verification_status === 'pending' || (user?.document_image_front && user?.document_id));
  const isRejected = user?.verification_status === 'rejected';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 max-w-2xl py-8 pb-24 md:pb-8">
        <button
          onClick={() => navigate('/client/dashboard')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition min-h-[44px]"
        >
          <ArrowLeft className="w-5 h-5" /> {t('clientProfile.backToDashboard', 'Volver al panel')}
        </button>

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#36C0C7] to-[#134168] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-[#111827]">
                {t('clientKyc.title', 'Verificación de Identidad (KYC)')}
              </h1>
              <p className="text-sm text-gray-500">
                {t('clientKyc.subtitle', 'Valida tu documento oficial para alquilar vehículos con total seguridad')}
              </p>
            </div>
          </div>

          {isVerified ? (
            <div className="flex flex-col items-center text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-gray-900">
                {t('clientKyc.verifiedTitle', '¡Tu identidad está verificada!')}
              </h2>
              <p className="text-gray-500 mt-1 mb-6 max-w-md">
                {t('clientKyc.verifiedDesc', 'Tu cuenta cuenta con el sello de verificación KYC. Ya puedes reservar y conducir vehículos en RedMobility sin restricciones.')}
              </p>
              <button
                onClick={() => navigate('/search')}
                className="px-6 py-3 rounded-full bg-[#134168] text-white font-bold hover:bg-[#0d2d47] transition shadow"
              >
                {t('clientKyc.exploreVehicles', 'Explorar vehículos disponibles')}
              </button>
            </div>
          ) : isPending && !successMsg ? (
            <div className="flex flex-col items-center text-center py-8">
              <Clock className="w-16 h-16 text-amber-500 mb-4 animate-pulse" />
              <h2 className="text-xl font-bold text-gray-900">
                {t('clientKyc.pendingTitle', 'Documento en revisión')}
              </h2>
              <p className="text-gray-500 mt-1 mb-6 max-w-md text-sm">
                {t('clientKyc.pendingDesc', 'Hemos recibido tus datos y documentos. Nuestro equipo de seguridad los revisará en las próximas 24 a 48 horas. Te notificaremos por correo electrónico una vez aprobada.')}
              </p>
              <div className="p-4 bg-gray-50 border border-gray-100 rounded-2xl w-full max-w-md text-left text-xs text-gray-600 mb-6 space-y-1">
                <p><b>Documento:</b> {user?.document_id || docNumber}</p>
                <p><b>Fotos cargadas:</b> {user?.document_image_front ? 'Frente cargado' : 'Pendiente'}{user?.document_image_back ? ' · Reverso cargado' : ''}</p>
              </div>
              <button
                onClick={() => navigate('/client/dashboard')}
                className="px-6 py-3 rounded-full bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition"
              >
                {t('clientProfile.backToDashboard', 'Volver al panel')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {isRejected && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm">
                  <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">{t('clientKyc.rejectedNotice', 'Tu verificación anterior fue rechazada.')}</p>
                    <p className="text-xs mt-0.5 text-red-600">
                      {user?.ban_reason || t('clientKyc.rejectedReasonFallback', 'Por favor verifica que las fotos sean nítidas, legibles y correspondan a un documento de identidad o licencia vigente.')}
                    </p>
                  </div>
                </div>
              )}

              {successMsg && (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 rounded-2xl p-4 text-sm">
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-green-600" />
                  <span>{successMsg}</span>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm">
                  <XCircle className="w-5 h-5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clientProfile.fullName', 'Nombre completo')}
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej. Juan Pérez"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clientProfile.whatsappPhone', 'Teléfono / WhatsApp')}
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1-809-555-0100"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('providerKyc.docTypeLabel', 'Tipo de documento')}
                  </label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] bg-white"
                  >
                    <option value="cedula">{t('providerKyc.docTypeCedulaOption', 'Cédula de identidad')}</option>
                    <option value="licencia">{t('clientKyc.docTypeLicencia', 'Licencia de conducir')}</option>
                    <option value="pasaporte">{t('providerKyc.docTypePassportOption', 'Pasaporte')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('providerKyc.docNumberLabel', 'Número de documento')}
                  </label>
                  <input
                    type="text"
                    required
                    value={docNumber}
                    onChange={(e) => setDocNumber(e.target.value)}
                    placeholder={docType === 'pasaporte' ? 'A1234567' : '001-1234567-8'}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                  />
                </div>
              </div>

              {/* Carga de fotos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('clientProfile.kycPhotosTitle', 'Fotos del Documento / Licencia (KYC)')}
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  {t('clientProfile.kycPhotosDesc', 'Sube una foto clara del anverso (frente) y reverso (atrás) de tu documento para verificar tu identidad.')}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Frente */}
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4 flex flex-col items-center justify-center text-center relative bg-gray-50/50 hover:bg-gray-50 transition min-h-[150px]">
                    {imageFront ? (
                      <div className="w-full relative group">
                        <img
                          src={imageFront}
                          alt="Frente"
                          className="w-full h-32 object-cover rounded-xl border border-gray-200"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                          <a
                            href={imageFront}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 bg-white text-gray-800 rounded-full shadow hover:bg-gray-100"
                            title="Ver imagen"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => setImageFront('')}
                            className="p-2 bg-red-600 text-white rounded-full shadow hover:bg-red-700"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-xs font-semibold text-green-700 flex items-center justify-center gap-1 mt-2">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Frente cargado
                        </span>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full py-4">
                        <Camera className="w-8 h-8 text-[#134168] mb-1.5" />
                        <span className="text-xs font-bold text-[#134168]">
                          {uploadingFront ? 'Subiendo...' : 'Subir Frente (Anverso)'}
                        </span>
                        <span className="text-[11px] text-gray-400 mt-0.5">JPG, PNG o WebP</span>
                        <input
                          type="file"
                          accept="image/*"
                          disabled={uploadingFront}
                          onChange={(e) => handleUpload(e, 'front')}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  {/* Reverso */}
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4 flex flex-col items-center justify-center text-center relative bg-gray-50/50 hover:bg-gray-50 transition min-h-[150px]">
                    {imageBack ? (
                      <div className="w-full relative group">
                        <img
                          src={imageBack}
                          alt="Reverso"
                          className="w-full h-32 object-cover rounded-xl border border-gray-200"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                          <a
                            href={imageBack}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 bg-white text-gray-800 rounded-full shadow hover:bg-gray-100"
                            title="Ver imagen"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => setImageBack('')}
                            className="p-2 bg-red-600 text-white rounded-full shadow hover:bg-red-700"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-xs font-semibold text-green-700 flex items-center justify-center gap-1 mt-2">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Reverso cargado
                        </span>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full py-4">
                        <Camera className="w-8 h-8 text-[#134168] mb-1.5" />
                        <span className="text-xs font-bold text-[#134168]">
                          {uploadingBack ? 'Subiendo...' : 'Subir Reverso (Atrás)'}
                        </span>
                        <span className="text-[11px] text-gray-400 mt-0.5">JPG, PNG o WebP</span>
                        <input
                          type="file"
                          accept="image/*"
                          disabled={uploadingBack}
                          onChange={(e) => handleUpload(e, 'back')}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#134168] text-white py-4 rounded-full font-bold text-lg hover:bg-[#0d2d47] transition-all disabled:opacity-50 shadow-md mt-6 flex items-center justify-center gap-2 min-h-[54px]"
              >
                <ShieldCheck className="w-5 h-5" />
                {submitting ? 'Enviando verificación…' : 'Enviar Documento para Verificación'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
